import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Retos.css";
import { getAuthHeader } from "../../utils/auth";
import { getApiBaseUrl } from "../../utils/api";

const OBJECTIVES = [
  {
    id: "happy",
    title: "Login válido",
    desc: "Acceso correcto con email y password válidos.",
    match: (text) => {
      const hasFlow =
        text.includes("login") || text.includes("acceso") || text.includes("credenciales");
      const hasSuccess =
        text.includes("valido") ||
        text.includes("correct") ||
        text.includes("dashboard") ||
        text.includes("home") ||
        text.includes("iniciar sesion") ||
        text.includes("abre sesion");

      return hasFlow && hasSuccess
        ? "Detectado como login correcto por referencias a acceso/login y resultado exitoso."
        : null;
    },
  },
  {
    id: "invalid-password",
    title: "Password incorrecta",
    desc: "El sistema bloquea el acceso y muestra un error genérico.",
    match: (text) => {
      const hasPassword = text.includes("password") || text.includes("contraseña");
      const hasFailure =
        text.includes("incorrect") || text.includes("error") || text.includes("credenciales invalidas");

      return hasPassword && hasFailure
        ? "Detectado por password/contraseña junto a fallo o error de credenciales."
        : null;
    },
  },
  {
    id: "invalid-email",
    title: "Formato de email inválido",
    desc: "Validación de cliente antes de enviar el formulario.",
    match: (text) => {
      const hasEmail = text.includes("email") || text.includes("correo");
      const hasValidation =
        text.includes("formato") || text.includes("invalido") || text.includes("validacion");

      return hasEmail && hasValidation
        ? "Detectado por email/correo junto a formato inválido o validación."
        : null;
    },
  },
  {
    id: "required-fields",
    title: "Campos obligatorios",
    desc: "Email y password vacíos o incompletos.",
    match: (text) => {
      const hasRequired =
        text.includes("vacio") || text.includes("obligatorio") || text.includes("requerido");
      const hasField = text.includes("email") || text.includes("password") || text.includes("contraseña");

      return hasRequired && hasField
        ? "Detectado por campos vacíos/obligatorios junto a email o password."
        : null;
    },
  },
  {
    id: "locked-user",
    title: "Usuario bloqueado",
    desc: "No permite entrar y comunica el motivo correctamente.",
    match: (text) =>
      text.includes("bloquead")
        ? "Detectado por referencia explícita a usuario o cuenta bloqueada."
        : null,
  },
  {
    id: "rate-limit",
    title: "Rate limit / demasiados intentos",
    desc: "Tras varios intentos fallidos el login se protege.",
    match: (text) =>
      text.includes("rate") || text.includes("intentos") || text.includes("throttle") || text.includes("bruteforce")
        ? "Detectado por rate limit, demasiados intentos o protección anti fuerza bruta."
        : null,
  },
  {
    id: "remember-me",
    title: "Recordar email / sesión",
    desc: "Persistencia del dato o sesión cuando se marca la casilla.",
    match: (text) => {
      const hasRememberAction =
        text.includes("recordar") ||
        text.includes("remember") ||
        text.includes("mantener sesion") ||
        text.includes("persist");

      const hasRememberContext =
        text.includes("checkbox") ||
        text.includes("casilla") ||
        text.includes("marcar") ||
        text.includes("desmarcar") ||
        text.includes("recordarme") ||
        text.includes("recordar email");

      return hasRememberAction && hasRememberContext
        ? "Detectado por referencias explícitas a la opción de recordar sesión/email."
        : null;
    },
  },
  {
    id: "reset-password",
    title: "Recuperar contraseña",
    desc: "Existe acceso a reset y el flujo responde bien.",
    match: (text) =>
      text.includes("recuper") || text.includes("reset")
        ? "Detectado por referencias a recuperar o resetear contraseña."
        : null,
  },
];

const OBSERVABLE_WORDS = [
  "muestra",
  "redirige",
  "bloquea",
  "permite",
  "mantiene",
  "guarda",
  "envia",
  "responde",
  "valida",
];

const normalize = (value) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export default function Reto1() {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();
  const [cases, setCases] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    steps: "",
    expected: "",
    priority: "Alta",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const addCase = () => {
    if (!form.title.trim() || !form.steps.trim() || !form.expected.trim()) {
      setError("Completa título, pasos y resultado esperado antes de añadir el caso.");
      return;
    }

    setCases((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        ...form,
      },
    ]);

    setForm({
      title: "",
      steps: "",
      expected: "",
      priority: "Alta",
    });
  };

  const removeCase = (id) => {
    setCases((prev) => prev.filter((testCase) => testCase.id !== id));
  };

  const evaluation = useMemo(() => {
    const covered = new Set();
    const qualityChecks = [];

    const reviewedCases = cases.map((testCase) => {
      const text = normalize(
        `${testCase.title} ${testCase.steps} ${testCase.expected} ${testCase.priority}`
      );

      const matches = OBJECTIVES.map((objective) => {
        const reason = objective.match(text);
        if (!reason) return null;

        covered.add(objective.id);

        return {
          id: objective.id,
          title: objective.title,
          reason,
        };
      }).filter(Boolean);

      const structure = [
        testCase.steps.trim().length >= 24,
        testCase.expected.trim().length >= 20,
        OBSERVABLE_WORDS.some((word) => normalize(testCase.expected).includes(word)),
      ];

      qualityChecks.push(...structure);

      return {
        ...testCase,
        matches,
        structureScore: structure.filter(Boolean).length,
      };
    });

    const coveredCount = covered.size;
    const structureTotal = qualityChecks.length || 1;
    const structurePassed = qualityChecks.filter(Boolean).length;
    const coveragePercent = (coveredCount / OBJECTIVES.length) * 80;
    const qualityPercent = (structurePassed / structureTotal) * 20;

    return {
      reviewedCases,
      covered,
      coveredCount,
      score: Math.round(Math.min(100, coveragePercent + qualityPercent)),
    };
  }, [cases]);

  const level =
    evaluation.score >= 90 ? "Senior QA mindset" :
    evaluation.score >= 75 ? "Cobertura sólida" :
    evaluation.score >= 50 ? "Buen enfoque, faltan riesgos" :
    "Todavía muy principiante";

  useEffect(() => {
    if (!cases.length || evaluation.score < 70 || !apiBaseUrl) return;

    fetch(`${apiBaseUrl}/api/auth/challenge-progress/complete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        challengeId: "reto1",
        title: "Login Test Designer",
        summary: "Has completado el reto manual de diseño de casos de prueba.",
      }),
    }).catch(() => {});
  }, [apiBaseUrl, cases.length, evaluation.score]);

  return (
    <div className="dash-page retos-page">
      <div className="retos-container">
        <div className="dash-hero retos-hero">
          <button
            className="dash-btn ghost roadmap"
            onClick={() => navigate("/retos")}
            style={{ marginBottom: 12 }}
          >
            ← Volver a retos
          </button>

          <div>
            <h1>Login Test Designer</h1>
            <p className="muted">
              Escenario realista: un login B2C con límite de intentos, usuario bloqueado,
              “recordarme” y recuperación de contraseña.
            </p>

            <div className="dash-quick">
              <div className="quick-card">
                <div className="quick-title">Cobertura</div>
                <div className="quick-value">{evaluation.score}%</div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${evaluation.score}%` }} />
                </div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Riesgos cubiertos</div>
                <div className="quick-value">{evaluation.coveredCount}/8</div>
                <div className="muted small">Cobertura automática según tus casos</div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Evaluación</div>
                <div className="quick-value small">{level}</div>
                <div className="muted small">Se valora cobertura y calidad del resultado esperado</div>
              </div>
            </div>
          </div>
        </div>

        <div className="retos-shell">
          <section className="reto-card scenario-card">
            <div className="reto-card-head">
              <h2>Escenario</h2>
              <span className="reto-badge">Reto 1 · Manual · 20 min</span>
            </div>

            <div className="scenario-grid">
              <div className="scenario-block">
                <div className="scenario-label">Producto</div>
                <p className="muted">
                  App web con login por email/password. Tras 5 intentos fallidos se bloquea
                  temporalmente la cuenta. Existe botón de “Recordarme” y enlace de recuperación.
                </p>
              </div>

              <div className="scenario-block">
                <div className="scenario-label">Objetivo del QA</div>
                <p className="muted">
                  Diseñar casos con pasos ejecutables y resultados observables que cubran
                  validación, seguridad y experiencia de usuario.
                </p>
              </div>
            </div>
          </section>

          <div className="reto-panel">
            <div className="reto-surface">
              <div className="reto-card-head">
                <h2>Añade un caso de prueba</h2>
                <span className="pill pill-gray">Estructura real</span>
              </div>

              <div className="form-grid">
                <label className="field-group">
                  <span>Título del caso</span>
                  <input
                    value={form.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="Ej: Login inválido muestra error genérico"
                  />
                </label>

                <label className="field-group">
                  <span>Prioridad</span>
                  <select
                    value={form.priority}
                    onChange={(e) => handleChange("priority", e.target.value)}
                  >
                    <option>Alta</option>
                    <option>Media</option>
                    <option>Baja</option>
                  </select>
                </label>
              </div>

              <label className="field-group">
                <span>Pasos</span>
                <textarea
                  rows={4}
                  value={form.steps}
                  onChange={(e) => handleChange("steps", e.target.value)}
                  placeholder="1. Abrir login 2. Introducir usuario bloqueado 3. Pulsar entrar"
                />
              </label>

              <label className="field-group">
                <span>Resultado esperado</span>
                <textarea
                  rows={4}
                  value={form.expected}
                  onChange={(e) => handleChange("expected", e.target.value)}
                  placeholder="El sistema muestra un mensaje genérico, no crea sesión y mantiene el formulario accesible."
                />
              </label>

              {error && <p className="error">{error}</p>}

              <button className="reto-btn reto-btn-inline" onClick={addCase}>
                Añadir caso
              </button>
            </div>

            <div className="reto-score">
              <div className="reto-score-num">{evaluation.score}%</div>
              <div className="muted small">{level}</div>
              <div className="progress">
                <div className="progress-bar" style={{ width: `${evaluation.score}%` }} />
              </div>

              <div className="validation-list">
                {OBJECTIVES.map((objective) => {
                  const done = evaluation.covered.has(objective.id);
                  return (
                    <div
                      key={objective.id}
                      className={`validation-item ${done ? "pass" : "pending"}`}
                    >
                      <div>
                        <strong>{objective.title}</strong>
                        <div className="muted small">{objective.desc}</div>
                      </div>
                      <span className="pill">{done ? "Cubierto" : "Pendiente"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <section className="reto-card wide">
            <div className="reto-card-head">
              <h2>Tus casos evaluados</h2>
              <span className="muted small">{cases.length} casos añadidos</span>
            </div>

            {evaluation.reviewedCases.length === 0 ? (
              <p className="muted">
                Todavía no has añadido casos. El reto empieza a puntuar cuando detecta riesgos
                reales y resultados esperados verificables.
              </p>
            ) : (
              <div className="case-review-list">
                {evaluation.reviewedCases.map((testCase) => (
                  <div key={testCase.id} className="case-review-card">
                    <div className="reto-card-head">
                      <div>
                        <div className="case-review-title">{testCase.title}</div>
                        <div className="muted small">Prioridad: {testCase.priority}</div>
                      </div>
                      <button className="mini-btn danger" onClick={() => removeCase(testCase.id)}>
                        Eliminar
                      </button>
                    </div>

                    <div className="case-review-body">
                      <div>
                        <div className="scenario-label">Pasos</div>
                        <p className="muted">{testCase.steps}</p>
                      </div>

                      <div>
                        <div className="scenario-label">Resultado esperado</div>
                        <p className="muted">{testCase.expected}</p>
                      </div>
                    </div>

                    <div className="meta-row">
                      {testCase.matches.length > 0 ? (
                        testCase.matches.map((match) => (
                          <span key={match.id} className="pill pill-green">
                            {match.title}
                          </span>
                        ))
                      ) : (
                        <span className="pill pill-gray">No cubre ningún riesgo detectado aún</span>
                      )}

                      <span className="pill pill-gray">
                        Calidad estructural: {testCase.structureScore}/3
                      </span>
                    </div>

                    {testCase.matches.length > 0 && (
                      <div className="match-reason-list">
                        {testCase.matches.map((match) => (
                          <div key={`${testCase.id}-${match.id}`} className="match-reason-item">
                            <strong>{match.title}:</strong> {match.reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
